import React, { useState } from 'react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown, Info } from 'lucide-react';
import { ReportEvidenceEditor } from './ReportEvidenceEditor';

let _uid = 0;
const nid = () => `local-${Date.now()}-${_uid++}`;

export const CURRENT_ASSESSMENT_OPTIONS = [
  ['strength', 'Strength'],
  ['on_track', 'On Track'],
  ['developing', 'Developing'],
  ['priority_development_area', 'Priority Development Area'],
  ['not_assessed', 'Not Assessed'],
];
export const REFERENCE_TYPE_OPTIONS = [
  ['pfap_staff_target', 'PFAP Staff Target'],
  ['age_development_reference', 'Age / Development Reference'],
  ['position_reference', 'Position Reference'],
  ['provider_reference', 'Provider Reference'],
  ['professional_context', 'Professional Context'],
  ['other', 'Other'],
];

// Sections that support a per-section staff assessment + display config.
export const MAJOR_ASSESSMENT_SECTIONS = [
  'football_performance', 'match_analysis', 'physical_gps', 'fitness',
  'biometrics', 'attendance', 'mental', 'pdp', 'injury_rehab',
];
export const MANUAL_TARGET_SECTIONS = ['physical_gps', 'fitness', 'biometrics'];

// Phase 92E.1 — sections with a dedicated report-specific evidence builder
// (images/PDFs/files/links attached to the section, independent of Football
// Areas / Priorities evidence). Scoped to GPS and Mental per spec.
export const SECTION_EVIDENCE_SECTIONS = ['physical_gps', 'mental'];

export const defaultConfig = () => ({ include_in_report: true, show_hub_data: true, show_staff_assessment: true });

// ── Per-section display config bar ──────────────────────────────────────────
export const SectionConfigBar = ({ config, onChange, hasData }) => {
  const c = { ...defaultConfig(), ...(config || {}) };
  const Toggle = ({ k, label }) => (
    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
      <input type="checkbox" checked={!!c[k]} onChange={(e) => onChange({ ...c, [k]: e.target.checked })} data-testid={`cfg-${k}`} />
      {label}
    </label>
  );
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="section-config-bar">
      <span className="text-xs font-medium text-slate-500">Parent report:</span>
      <Toggle k="include_in_report" label="Include in report" />
      <Toggle k="show_hub_data" label="Show Hub data" />
      <Toggle k="show_staff_assessment" label="Show staff assessment" />
      {!hasData && <span className="ml-auto text-[11px] text-slate-400">No Hub data — you can still write a staff assessment.</span>}
    </div>
  );
};

// ── Per-section staff assessment ────────────────────────────────────────────
export const StaffAssessmentEditor = ({ value, onChange, onCommit }) => (
  <div className="mt-4" data-testid="staff-assessment">
    <div className="mb-1.5 flex items-center gap-1.5">
      <label className="text-sm font-medium text-slate-700">Staff Assessment</label>
      <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><Info className="h-3 w-3" /> May appear in the finalized player/parent report</span>
    </div>
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      rows={5}
      placeholder="What does this information mean for this player? Write a clear, parent-friendly professional assessment…"
      data-testid="staff-assessment-textarea"
    />
  </div>
);

const Field = ({ label, children }) => (
  <div><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>{children}</div>
);

// ── Manual Football Assessment Areas ────────────────────────────────────────
export const FootballAreasEditor = ({ items, setItems, onCommit, reportId }) => {
  const [openId, setOpenId] = useState(null);
  const update = (id, patch) => setItems(items.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const add = () => { const id = nid(); const next = [...items, { id, title: '', current_assessment: '', observation: '', doing_well: '', needs_improvement: '', why_matters: '', expected_standard: '', development_focus: '', order: items.length }]; setItems(next); onCommit(next); setOpenId(id); };
  const remove = (id) => { const next = items.filter((a) => a.id !== id).map((a, i) => ({ ...a, order: i })); setItems(next); onCommit(next); };
  const move = (id, d) => { const i = items.findIndex((a) => a.id === id); const j = i + d; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; const r = n.map((a, k) => ({ ...a, order: k })); setItems(r); onCommit(r); };
  return (
    <div className="mt-4 space-y-3" data-testid="football-areas-editor">
      <div className="text-sm font-medium text-slate-700">Manual Football Assessment Areas</div>
      {items.map((a, i) => {
        const open = openId === a.id;
        return (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white" data-testid={`fb-area-${i}`}>
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button type="button" className="flex-1 text-left text-sm font-medium text-slate-800 truncate" onClick={() => setOpenId(open ? null : a.id)} data-testid={`fb-area-toggle-${i}`}>
                {a.title || <span className="text-slate-400">Untitled area — click to edit</span>}
              </button>
              {a.current_assessment && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{(CURRENT_ASSESSMENT_OPTIONS.find((o) => o[0] === a.current_assessment) || [])[1]}</span>}
              <button type="button" onClick={() => move(a.id, -1)} className="text-slate-300 hover:text-slate-600" data-testid={`fb-area-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(a.id, 1)} className="text-slate-300 hover:text-slate-600" data-testid={`fb-area-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(a.id)} className="text-slate-300 hover:text-rose-600" data-testid={`fb-area-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            {open && (
              <div className="space-y-2 border-t border-slate-100 px-3 py-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Field label="Area / Title"><Input value={a.title} onChange={(e) => update(a.id, { title: e.target.value })} onBlur={() => onCommit(items)} placeholder="e.g. In Possession" data-testid={`fb-area-title-${i}`} /></Field>
                  <Field label="Current Assessment">
                    <select value={a.current_assessment || ''} onChange={(e) => { update(a.id, { current_assessment: e.target.value }); onCommit(items.map((x) => x.id === a.id ? { ...x, current_assessment: e.target.value } : x)); }} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`fb-area-status-${i}`}>
                      <option value="">Select…</option>
                      {CURRENT_ASSESSMENT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Observation"><Textarea value={a.observation || ''} onChange={(e) => update(a.id, { observation: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`fb-area-observation-${i}`} /></Field>
                <Field label="What the player is doing well"><Textarea value={a.doing_well || ''} onChange={(e) => update(a.id, { doing_well: e.target.value })} onBlur={() => onCommit(items)} rows={2} /></Field>
                <Field label="What needs improvement"><Textarea value={a.needs_improvement || ''} onChange={(e) => update(a.id, { needs_improvement: e.target.value })} onBlur={() => onCommit(items)} rows={2} /></Field>
                <Field label="Why this matters"><Textarea value={a.why_matters || ''} onChange={(e) => update(a.id, { why_matters: e.target.value })} onBlur={() => onCommit(items)} rows={2} /></Field>
                <Field label="Expected standard"><Textarea value={a.expected_standard || ''} onChange={(e) => update(a.id, { expected_standard: e.target.value })} onBlur={() => onCommit(items)} rows={2} /></Field>
                <Field label="Development focus / next action"><Textarea value={a.development_focus || ''} onChange={(e) => update(a.id, { development_focus: e.target.value })} onBlur={() => onCommit(items)} rows={2} /></Field>
                <ReportEvidenceEditor items={a.evidence || []} reportId={reportId} onChange={(next) => { const merged = items.map((x) => x.id === a.id ? { ...x, evidence: next } : x); setItems(merged); onCommit(merged); }} testId={`fb-area-evidence-${i}`} />
              </div>
            )}
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={add} data-testid="fb-area-add"><Plus className="mr-1 h-4 w-4" /> Add Assessment Area</Button>
    </div>
  );
};

// ── Report-specific manual targets ──────────────────────────────────────────
export const ManualTargetsEditor = ({ sectionKey, items, setItems, onCommit }) => {
  const mine = items.filter((t) => t.section_key === sectionKey);
  const others = items.filter((t) => t.section_key !== sectionKey);
  const commit = (nextMine) => { const merged = [...others, ...nextMine]; setItems(merged); onCommit(merged); };
  const update = (id, patch) => setItems([...others, ...mine.map((t) => (t.id === id ? { ...t, ...patch } : t))]);
  const add = () => commit([...mine, { id: nid(), section_key: sectionKey, label: '', current_value: '', unit: '', target_value: '', reference_type: 'pfap_staff_target', context: '', explanation: '', order: mine.length }]);
  const remove = (id) => commit(mine.filter((t) => t.id !== id));
  return (
    <div className="mt-4 space-y-3" data-testid={`manual-targets-${sectionKey}`}>
      <div className="text-sm font-medium text-slate-700">Report-Specific Staff Targets <span className="font-normal text-xs text-slate-400">(not a validated PFAP benchmark)</span></div>
      {mine.map((t, i) => (
        <div key={t.id} className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-3" data-testid={`manual-target-${sectionKey}-${i}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Field label="Label"><Input value={t.label} onChange={(e) => update(t.id, { label: e.target.value })} onBlur={() => onCommit([...others, ...mine])} data-testid={`mt-label-${sectionKey}-${i}`} /></Field>
            <Field label="Current"><Input value={t.current_value || ''} onChange={(e) => update(t.id, { current_value: e.target.value })} onBlur={() => onCommit([...others, ...mine])} data-testid={`mt-current-${sectionKey}-${i}`} /></Field>
            <Field label="Target"><Input value={t.target_value || ''} onChange={(e) => update(t.id, { target_value: e.target.value })} onBlur={() => onCommit([...others, ...mine])} data-testid={`mt-target-${sectionKey}-${i}`} /></Field>
            <Field label="Unit"><Input value={t.unit || ''} onChange={(e) => update(t.id, { unit: e.target.value })} onBlur={() => onCommit([...others, ...mine])} data-testid={`mt-unit-${sectionKey}-${i}`} /></Field>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Reference type">
              <select value={t.reference_type || 'pfap_staff_target'} onChange={(e) => { update(t.id, { reference_type: e.target.value }); onCommit([...others, ...mine.map((x) => x.id === t.id ? { ...x, reference_type: e.target.value } : x)]); }} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`mt-reference-${sectionKey}-${i}`}>
                {REFERENCE_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Context / explanation"><Input value={t.context || ''} onChange={(e) => update(t.id, { context: e.target.value })} onBlur={() => onCommit([...others, ...mine])} placeholder="e.g. PFAP staff target for next review" data-testid={`mt-context-${sectionKey}-${i}`} /></Field>
          </div>
          <div className="mt-2 text-right"><button type="button" onClick={() => remove(t.id)} className="text-xs text-rose-500 hover:underline" data-testid={`mt-remove-${sectionKey}-${i}`}>Remove target</button></div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid={`mt-add-${sectionKey}`}><Plus className="mr-1 h-4 w-4" /> Add Report Target</Button>
    </div>
  );
};

// ── Custom report sections ──────────────────────────────────────────────────
export const CustomSectionsEditor = ({ items, setItems, onCommit }) => {
  const update = (id, patch) => setItems(items.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const add = () => { const next = [...items, { id: nid(), title: '', body: '', order: items.length, include_in_report: true }]; setItems(next); onCommit(next); };
  const remove = (id) => { const next = items.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })); setItems(next); onCommit(next); };
  const move = (id, d) => { const i = items.findIndex((s) => s.id === id); const j = i + d; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; const r = n.map((s, k) => ({ ...s, order: k })); setItems(r); onCommit(r); };
  return (
    <div className="space-y-3" data-testid="custom-sections-editor">
      {items.length === 0 && <p className="text-sm text-slate-400">No custom sections. Add one for content not covered by the standard template.</p>}
      {items.map((s, i) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`custom-section-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <Input value={s.title} onChange={(e) => update(s.id, { title: e.target.value })} onBlur={() => onCommit(items)} placeholder="Section title" data-testid={`custom-title-${i}`} />
            <label className="inline-flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap"><input type="checkbox" checked={s.include_in_report !== false} onChange={(e) => { update(s.id, { include_in_report: e.target.checked }); onCommit(items.map((x) => x.id === s.id ? { ...x, include_in_report: e.target.checked } : x)); }} /> Include</label>
            <button type="button" onClick={() => move(s.id, -1)} className="text-slate-300 hover:text-slate-600"><ChevronUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => move(s.id, 1)} className="text-slate-300 hover:text-slate-600"><ChevronDown className="h-4 w-4" /></button>
            <button type="button" onClick={() => remove(s.id)} className="text-slate-300 hover:text-rose-600" data-testid={`custom-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <Textarea value={s.body || ''} onChange={(e) => update(s.id, { body: e.target.value })} onBlur={() => onCommit(items)} rows={4} placeholder="Section content…" data-testid={`custom-body-${i}`} />
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="custom-add"><Plus className="mr-1 h-4 w-4" /> Add Custom Section</Button>
    </div>
  );
};
